/**
 * Issue Analysis API service — triggers the 3-phase preliminary analysis pipeline.
 */

import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';
import { ApiError } from './api';

/** Confidence flag for extracted entities. */
export type ConfidenceFlag = 'high' | 'medium' | 'low';

/** Explicit (table, column) binding for an entity value — the column in a
 *  specific table that the value should be queried against. */
export interface EntityBinding {
  table_fqn: string;
  column: string;
}

/** A trackable entity extracted from the JIRA ticket. */
export interface ExtractedEntity {
  /** Display label (e.g. "Booking", "Bill of Lading"). Cosmetic when bindings are set. */
  entity_type: string;
  value: string;
  confidence: ConfidenceFlag;
  confidence_reason: string;
  /** Explicit (table, column) bindings. When present, retrieval uses them
   *  directly instead of guessing via candidate columns. */
  bindings?: EntityBinding[];
}

/** Attachment metadata from JIRA. */
export interface AttachmentInfo {
  filename: string;
  content_url: string | null;
  thumbnail_url: string | null;
  mime_type: string | null;
  size: number | null;
}

/** A field in a table that matches a detected entity type. */
export interface EntityFieldMatch {
  entity_type: string;
  matched_field: string;
}

/** A single field (column) of a table, returned on demand by `getTableFields`. */
export interface TableField {
  name: string;
  data_type: string;
  description: string;
}

/** A table belonging to a module, from Neo4j. */
export interface ModuleTable {
  name: string;
  fqn: string;
  database: string;
  description: string;
  module: string;
  /** Primary key column name, if defined on the Table node. */
  primary_key?: string | null;
  /** All field names in this table — used by the UI to derive entity-type suggestions from the selected modules. */
  fields?: string[];
  /** Fields in this table that match detected entity types (populated when entity_types are sent). */
  entity_matches?: EntityFieldMatch[];
  /**
   * `true` when the backend identifies this table as a scratch / archive /
   * log surface (`tmp*`, `*_arch`, `*_log`, `*_tab_edited`, `*_history`,
   * `*_bak`). Such tables almost always return zero rows for entity
   * lookups; the UI greys them out and never auto-selects them.
   */
  excluded?: boolean;
  /** Frequency tier from historical issue data: "high" (freq_used=true), "rare" (freq_used=false), "unknown" (no data). */
  frequency_tier?: string;
  /** Frequency score (0-1000) for sorting and ranking. */
  frequency_score?: number;
  /** ISO timestamp when frequency data was last synced. */
  freq_synced_at?: string;
  /** Row count from carrier-specific HAS_ROWCOUNT edges. */
  row_count?: number;
  /** Whether this table has data (row_count > 0). */
  has_data?: boolean;
}

/** Recommendation tier for a table. */
export type TableTier = 'auto_select' | 'recommended' | 'normal' | 'rarely_used' | 'hidden';

/** Learned table recommendation. */
export interface TableRecommendation {
  table_fqn: string;
  tier: TableTier;
  selection_rate: number;
  sample_count: number;
}

/** Structured ticket from Phase 1 intake. */
export interface StructuredTicket {
  ticket_key: string;
  summary: string;
  description: string | null;
  project: string | null;
  assignee: string | null;
  reporter: string | null;
  created: string | null;
  updated: string | null;
  resolution: string | null;
  steps_to_reproduce: string | null;
  affected_client: string | null;
  environment: string | null;
  product_version: string | null;
  deployment_region: string | null;
  images_received: boolean;
  attachments: string[];
  attachment_details: AttachmentInfo[];
  image_warning: string | null;
  entities: ExtractedEntity[];
  modules: string[];
  /** All detected modules with relevance scores and source. */
  detected_modules?: DetectedModule[];
  issue_type: string;
  labels: string[];
  components: string[];
  priority: string | null;
  status: string;
  /** JIRA comments (newest first). */
  comments?: TicketComment[];
  /** Linked JIRA issues. */
  linked_issues?: LinkedIssue[];
  /** LLM-condensed summary of the full ticket (~150 words). */
  condensed_summary?: string | null;
  /** Text extracted from image attachments via Claude vision. */
  extracted_image_text?: string | null;
  /** Text read from document attachments (xlsx/csv/txt/pdf/docx/msg) —
   *  one "=== Document: name ===" section per file (1.68.8). */
  extracted_document_text?: string | null;
  /** Per-image OCR extraction results (success/failed/skipped with preview). */
  image_extraction_results?: ImageExtractionResult[];
  /** Support docs retrieved during intake (drives module enrichment). */
  document_validations?: DocumentValidation[];
  /** LLM-synthesised answer from support docs, fetched during intake. */
  support_docs_answer?: string | null;
  /** Knowledge-bank matches retrieved during intake. */
  similar_issues?: SimilarIssue[];
}

/** Per-image OCR extraction result. */
export interface ImageExtractionResult {
  filename: string;
  /** "success", "failed", or "skipped" */
  status: 'success' | 'failed' | 'skipped';
  /** Reason for failure/skip, or note like "No text detected". */
  detail?: string | null;
  /** First ~200 chars of extracted text (success only). */
  text_preview?: string | null;
}

/** A detected module with relevance score and detection source. */
export interface DetectedModule {
  name: string;
  /** "high", "medium", or "low" */
  relevance: 'high' | 'medium' | 'low';
  /** Where detection came from: "llm", "keyword-sweep", "enrichment-llm", etc. */
  source: string;
}

/** A comment on the JIRA ticket. */
export interface TicketComment {
  author: string;
  body: string;
  created: string;
}

/** A linked JIRA issue. */
export interface LinkedIssue {
  relationship: string;
  key: string;
  summary: string;
  status: string;
}

/** SQL data extraction result. */
export interface SqlDataResult {
  entity_type: string;
  entity_value: string;
  tables_queried: string[];
  data_found: boolean;
  data: unknown;
  message: string | null;
  /** SQL query generated and executed by ERAX (if available). */
  erax_sql_query?: string | null;
  /** Raw data rows returned by ERAX (JSON array). */
  erax_data?: unknown;
  /** ERAX narrative response (markdown summary of the data). */
  erax_response?: string | null;
}

/** Timeline event. */
export interface TimelineEvent {
  timestamp: string;
  event_type: string;
  actor: string;
  state_transition: string | null;
  details: string | null;
}

/** Timeline result for an entity. */
export interface TimelineResult {
  entity_type: string;
  entity_value: string;
  generated: boolean;
  events: TimelineEvent[];
  message: string | null;
  /** SQL queries that were attempted for timeline generation (for debugging). */
  queries_attempted?: string[];
}

/** A support document chunk retrieved via RAG search. */
export interface DocumentValidation {
  doc_title: string;
  doc_type: string;
  category: string;
  section_hierarchy: string[];
  content: string;
  score: number;
  source_path: string;
}

/** Similar past issue from the knowledge bank. */
export interface SimilarIssue {
  reference: string;
  summary: string;
  module: string;
  entity_type: string | null;
  root_cause: string;
  resolution: string;
  similarity_score: number;
}

/** Data gap — what was looked for but not found. */
export interface DataGap {
  looked_for: string;
  searched_in: string;
  recommended_action: string;
}

/** Support Docs / Knowledge Bank artifacts fetched during `/intake`, carried
 *  forward into `/analyze`. The confirmed path skips re-running intake, so
 *  resending these keeps the Support Docs tab populated instead of dropping
 *  the documents the intake already found. */
export interface IntakeArtifacts {
  document_validations?: DocumentValidation[];
  support_docs_answer?: string | null;
  similar_issues?: SimilarIssue[];
  /** Per-attachment read/skip/fail statuses — the record of what the
   *  analysis actually read. Dropped before 1.72.2, which made a partial
   *  read indistinguishable from a complete one. */
  image_extraction_results?: ImageExtractionResult[];
  /** OCR text + document text from intake — without these the confirmed
   *  path stores null (GWFC-8337) and Compass never sees screenshot text. */
  extracted_image_text?: string | null;
  extracted_document_text?: string | null;
}

/** Attach carried-forward intake artifacts to an analyze request body. Only
 *  non-empty values are sent, so the full-intake path (which carries none)
 *  lets the backend compute them fresh. */
function applyIntakeArtifacts(body: Record<string, unknown>, a?: IntakeArtifacts): void {
  if (!a) return;
  if (a.document_validations && a.document_validations.length > 0) {
    body.document_validations = a.document_validations;
  }
  if (a.support_docs_answer) body.support_docs_answer = a.support_docs_answer;
  if (a.similar_issues && a.similar_issues.length > 0) {
    body.similar_issues = a.similar_issues;
  }
  if (a.image_extraction_results && a.image_extraction_results.length > 0) {
    body.image_extraction_results = a.image_extraction_results;
  }
  if (a.extracted_image_text) body.extracted_image_text = a.extracted_image_text;
  if (a.extracted_document_text) body.extracted_document_text = a.extracted_document_text;
}

/** Phase 2 retrieval results. */
/** SQL script solution for fixing issues (v1: semantic match on issue_summary). */
export interface SqlScriptSolution {
  id: number;
  original_jira?: string | null;
  issue_summary: string;
  root_cause: string;
  impact_description: string;
  sql_script: string;
  screen_name?: string | null;
  module_category?: string | null;
}

export interface RetrievalResults {
  sql_results: SqlDataResult[];
  timelines: TimelineResult[];
  document_validations: DocumentValidation[];
  /** LLM-synthesized answer from support documents (markdown). */
  support_docs_answer?: string | null;
  similar_issues: SimilarIssue[];
  /** Recommended SQL scripts matching the issue (semantic match on issue_summary text). */
  applicable_scripts?: SqlScriptSolution[];
  gaps: DataGap[];
}

/** Diagnosis hypothesis. */
export interface DiagnosisHypothesis {
  statement: string;
  supporting_evidence: string[];
  contradicting_evidence: string[];
  confidence: ConfidenceFlag;
  confidence_score: number;
}

/** Root cause analysis. */
export interface RootCauseAnalysis {
  causal_chain: string;
  supporting_data_points: string[];
  determined: boolean;
  additional_info_required: string[];
}

/** Workaround plan. */
export interface WorkaroundPlan {
  steps: string[];
}

/** Permanent fix plan. */
export interface FixPlan {
  description: string;
  module_affected: string;
  effort_estimate: string;
  escalation_path: string | null;
}

/** Resolution plan. */
export interface ResolutionPlan {
  immediate_workaround: WorkaroundPlan | null;
  permanent_fix: FixPlan | null;
  knowledge_bank_reference: string | null;
}

/** Information inventory. */
export interface InformationInventory {
  entities_found: ExtractedEntity[];
  sql_data_summary: string[];
  timelines_generated: string[];
  documents_matched: string[];
  past_issues_matched: string[];
  gaps: DataGap[];
}

/** Structured analysis output. */
export interface CauseChain {
  immediate_cause: string;
  root_cause: string;
  consequence: string;
}

export interface ActionSplit {
  fix: string;
  prevent: string;
  escalate: string;
}

export interface EvidencePair {
  claim: string;
  evidence: string;
}

export interface LayeredRca {
  story: string;
  defect_class: string;
  cause_chain: CauseChain;
  evidence: EvidencePair[];
  action_split: ActionSplit;
}

export interface StructuredAnalysis {
  issue_definition: string;
  information_inventory: InformationInventory;
  differential_diagnosis: DiagnosisHypothesis[];
  root_cause_analysis: RootCauseAnalysis;
  resolution_plan: ResolutionPlan;
  layered_rca?: LayeredRca | null;
}

/** Full issue analysis response from the backend. */
export interface IssueAnalysisResponse {
  structured_ticket: StructuredTicket;
  retrieval_results: RetrievalResults;
  analysis: StructuredAnalysis;
  duration_ms: number;
  warnings: string[];
  /** Whether this result was served from the DB cache. */
  cached?: boolean;
  /** Database row ID of this analysis (for retrieval). */
  analysis_id?: number;
  /** When this analysis was originally run. */
  analyzed_at?: string;
  /** ISO timestamp when the analyst marked this RCA as verified. Null/absent
   * when unverified. Cleared automatically if the analysis is re-run. */
  verified_at?: string | null;
  /** Identifier (email preferred) of the analyst who verified it. */
  verified_by?: string | null;
  /** Email of the analyst who RAN it. Absent for rows predating migration
   *  040 — genuinely unknown, so nothing is shown rather than a guess. */
  analyzed_by?: string | null;
  /** Free-text final comment shown next to the RCA (≤1000 chars). */
  analyst_comment?: string | null;
  analyst_comment_updated_at?: string | null;
  analyst_comment_updated_by?: string | null;
  /** Pre-analysis comment supplied at the Analyze step (≤2000 chars). Fed
   * into the Phase 1 / Phase 3 / Final Summary LLM prompts and persisted
   * for later retrieval. Editable as a note after analysis (no re-run). */
  user_input_comment?: string | null;
  user_input_comment_updated_at?: string | null;
  user_input_comment_updated_by?: string | null;
}

/** Analyst-managed entity-type definition (from Setup → Entity Types). */
export interface EntityTypeDefinition {
  id: number;
  name: string;
  /** Comma-separated alternative labels the LLM might emit. */
  aliases: string | null;
  /** 1 (highest) to 4 (lowest, always dropped). */
  tier: number;
  /** 'eq' or 'like'. */
  match_op: string;
  queryable: boolean;
  /** Comma-separated column names to try when binding values. */
  column_candidates: string | null;
  description: string | null;
  created_by: string;
  created_at: string;
  disabled_at: string | null;
  disabled_by: string | null;
}

/** Analyst-curated hint from the AI Summary panel. */
export interface ModuleHint {
  id: number;
  hint_type: 'module' | 'entity_value' | 'entity_type';
  selected_text: string;
  module_name: string | null;
  table_fqn: string | null;
  field_name: string | null;
  /** Only set when `hint_type = 'entity_type'`. */
  corrected_entity_type: string | null;
  source_ticket: string | null;
  created_by: string;
  confirmed_count: number;
  created_at: string;
  last_used_at: string | null;
  disabled_at: string | null;
  disabled_by: string | null;
}

/** Summary of a past analysis run (lightweight — no full payloads). */
export interface AnalysisSummary {
  id: number;
  ticket_key: string;
  summary: string;
  duration_ms: number;
  warning_count: number;
  created_at: string;
  verified_at?: string | null;
  verified_by?: string | null;
  /** RCA determined flag; null when the row has no RCA. */
  determined?: boolean | null;
}

/** Response from the intake endpoint (Phase 1 only). */
export interface IntakeResponse {
  structured_ticket: StructuredTicket;
}

export const issueAnalysisApi = {
  /**
   * Step 1 — Run Phase 1 (Intake) only.
   * Returns the structured ticket with auto-detected entities and modules
   * for the user to confirm/edit before running the full analysis.
   */
  async intake(ticketKey: string, signal?: AbortSignal): Promise<IntakeResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/intake`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ticket_key: ticketKey }),
      signal,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Intake failed (${response.status})`);
    }

    return response.json();
  },

  /**
   * SSE-streaming variant of intake(). Emits per-step progress events so the
   * frontend can show what the pipeline is doing during the 10–30s intake.
   */
  async intakeStream(
    ticketKey: string,
    signal: AbortSignal | undefined,
    onProgress?: (stage: string) => void,
  ): Promise<IntakeResponse> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/intake-stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ticket_key: ticketKey }),
      signal,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Intake failed (${response.status})`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: IntakeResponse | null = null;
    let currentEvent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ') && currentEvent) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            if (currentEvent === 'progress' && parsed.stage) {
              onProgress?.(parsed.stage);
            } else if (currentEvent === 'result') {
              result = parsed;
            } else if (currentEvent === 'error') {
              throw new Error(parsed.message || 'Intake failed');
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e;
          }
          currentEvent = '';
        } else if (line.trim() === '') {
          currentEvent = '';
        }
      }
    }

    if (!result) {
      throw new Error('Intake completed without returning a result');
    }

    return result;
  },

  /**
   * Fetch learned table recommendations for a module + entity_type.
   */
  async getTableRecommendations(
    module: string,
    entityType: string,
  ): Promise<{ recommendations: TableRecommendation[] }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/table-recommendations`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ module, entity_type: entityType }),
    });
    if (!response.ok) return { recommendations: [] };
    return response.json();
  },

  /**
   * Log table selections for learning.
   */
  async logTableSelections(
    module: string,
    entityType: string,
    ticketKey: string,
    selections: { table_fqn: string; selected: boolean }[],
  ): Promise<void> {
    const headers = await getAuthHeaders();
    await fetchApi(`${API_BASE}/issue-analysis/log-table-selections`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ module, entity_type: entityType, ticket_key: ticketKey, selections }),
    }).catch(() => {}); // Fire-and-forget, don't block analysis
  },

  /**
   * Step 2 — Run the full 3-phase preliminary analysis on a JIRA ticket.
   * Accepts optional confirmed_modules and confirmed_entities from the user.
   * Returns cached result if available (unless forceRefresh=true).
   */
  /**
   * Fetch the full list of module names from Neo4j (for typeahead/autocomplete
   * in the module editor). Returns names sorted alphabetically.
   */
  async listModules(): Promise<{ modules: string[]; count: number }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/modules`, {
      method: 'GET',
      headers,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to fetch modules (${response.status})`);
    }
    return response.json();
  },

  /**
   * Fetch the fields (name, type, description) for a single table on demand.
   * Used by the table-details expansion in the Issue Analysis panel so
   * descriptions aren't shipped for every table up front.
   */
  async getTableFields(fqn: string): Promise<{ fqn: string; fields: TableField[] }> {
    const headers = await getAuthHeaders();
    const url = `${API_BASE}/issue-analysis/table-fields?fqn=${encodeURIComponent(fqn)}`;
    const response = await fetchApi(url, { method: 'GET', headers });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to fetch table fields (${response.status})`);
    }
    return response.json();
  },

  /**
   * Fetch available tables for given modules from Neo4j.
   */
  async getTablesForModules(modules: string[], entityTypes?: string[], ticketKey?: string): Promise<{ tables: ModuleTable[] }> {
    const headers = await getAuthHeaders();
    const body: Record<string, unknown> = { modules };
    if (entityTypes && entityTypes.length > 0) {
      body.entity_types = entityTypes;
    }
    if (ticketKey) {
      body.ticket_key = ticketKey;
    }
    const response = await fetchApi(`${API_BASE}/issue-analysis/tables`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to fetch tables (${response.status})`);
    }
    return response.json();
  },

  async analyze(
    ticketKey: string,
    signal?: AbortSignal,
    forceRefresh = false,
    confirmedModules?: string[],
    confirmedEntities?: ExtractedEntity[],
    confirmedTables?: string[],
    skipDataRetrieval = false,
    intakeArtifacts?: IntakeArtifacts,
  ): Promise<IssueAnalysisResponse> {
    const headers = await getAuthHeaders();
    const body: Record<string, unknown> = {
      ticket_key: ticketKey,
      force_refresh: forceRefresh,
    };
    if (confirmedModules) body.confirmed_modules = confirmedModules;
    if (confirmedEntities) body.confirmed_entities = confirmedEntities;
    if (confirmedTables) body.confirmed_tables = confirmedTables;
    // "Ignore Table details" mode — skip ERAX SQL + timeline retrieval
    // for clarification-style tickets that have no entity identifiers.
    if (skipDataRetrieval) body.skip_data_retrieval = true;
    applyIntakeArtifacts(body, intakeArtifacts);

    const response = await fetchApi(`${API_BASE}/issue-analysis/analyze`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Issue analysis failed (${response.status})`);
    }

    return response.json();
  },

  /**
   * SSE-streaming variant of analyze(). Returns the same result but emits
   * progress events so the frontend can display the current pipeline stage.
   */
  async analyzeStream(
    ticketKey: string,
    signal: AbortSignal | undefined,
    forceRefresh: boolean,
    confirmedModules?: string[],
    confirmedEntities?: ExtractedEntity[],
    confirmedTables?: string[],
    skipDataRetrieval?: boolean,
    onProgress?: (stage: string) => void,
    userComment?: string,
    intakeArtifacts?: IntakeArtifacts,
  ): Promise<IssueAnalysisResponse> {
    const headers = await getAuthHeaders();
    const body: Record<string, unknown> = {
      ticket_key: ticketKey,
      force_refresh: forceRefresh,
    };
    if (confirmedModules) body.confirmed_modules = confirmedModules;
    if (confirmedEntities) body.confirmed_entities = confirmedEntities;
    if (confirmedTables) body.confirmed_tables = confirmedTables;
    if (skipDataRetrieval) body.skip_data_retrieval = true;
    if (userComment !== undefined && userComment.trim() !== '') {
      body.user_comment = userComment;
    }
    applyIntakeArtifacts(body, intakeArtifacts);

    const response = await fetchApi(`${API_BASE}/issue-analysis/analyze-stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Issue analysis failed (${response.status})`);
    }

    const reader = response.body!.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let result: IssueAnalysisResponse | null = null;
    let currentEvent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('event: ')) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith('data: ') && currentEvent) {
          const data = line.slice(6);
          try {
            const parsed = JSON.parse(data);
            if (currentEvent === 'progress' && parsed.stage) {
              onProgress?.(parsed.stage);
            } else if (currentEvent === 'result') {
              result = parsed;
            } else if (currentEvent === 'error') {
              throw new Error(parsed.message || 'Analysis failed');
            }
          } catch (e) {
            if (e instanceof Error && e.message !== 'Unexpected end of JSON input') throw e;
          }
          currentEvent = '';
        } else if (line.trim() === '') {
          currentEvent = '';
        }
      }
    }

    if (!result) {
      throw new Error('Analysis completed without returning a result');
    }

    return result;
  },

  /**
   * Retrieve the latest (or specific) saved analysis for a ticket.
   */
  async getAnalysis(ticketKey: string, analysisId?: number): Promise<IssueAnalysisResponse> {
    const headers = await getAuthHeaders();
    const params = analysisId ? `?id=${analysisId}` : '';
    const response = await fetchApi(`${API_BASE}/issue-analysis/${encodeURIComponent(ticketKey)}${params}`, {
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new ApiError(
        data?.detail || `Failed to fetch analysis (${response.status})`,
        response.status,
        data,
      );
    }

    return response.json();
  },

  /**
   * List past analysis runs for a ticket (summaries only).
   */
  async listHistory(ticketKey: string, limit = 20): Promise<{ ticket_key: string; analyses: AnalysisSummary[]; total: number }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/${encodeURIComponent(ticketKey)}/history?limit=${limit}`, {
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to list analysis history (${response.status})`);
    }

    return response.json();
  },

  /**
   * List the most recent analyses across all tickets.
   */
  async listRecent(
    opts: {
      limit?: number;
      offset?: number;
      q?: string;
      verified?: boolean;
      withWarnings?: boolean;
    } = {},
  ): Promise<{ analyses: AnalysisSummary[]; total: number; limit: number; offset: number }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    params.set('limit', String(opts.limit ?? 20));
    if (opts.offset != null) params.set('offset', String(opts.offset));
    if (opts.q && opts.q.trim()) params.set('q', opts.q.trim());
    if (opts.verified != null) params.set('verified', String(opts.verified));
    if (opts.withWarnings != null) params.set('with_warnings', String(opts.withWarnings));

    const response = await fetchApi(`${API_BASE}/issue-analysis/recent?${params.toString()}`, {
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to list recent analyses (${response.status})`);
    }

    return response.json();
  },

  /**
   * Generate (or retrieve cached) plain-English final analysis summary.
   * Sends only the analysis_id — the backend loads data from the DB and
   * caches the LLM-generated summary for subsequent requests.
   */
  /**
   * Mark (or unmark) an analysis as analyst-verified. The verified flag is
   * the trust signal that feeds the Q&A learning loop — only verified
   * analyses become candidates. Cleared automatically on re-analysis.
   */
  async setVerified(
    analysisId: number,
    verified: boolean,
  ): Promise<{ analysis_id: number; verified: boolean; verified_at?: string; verified_by?: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/by-id/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ analysis_id: analysisId, verified }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to update verification (${response.status})`);
    }
    return response.json();
  },

  /**
   * Save (or clear) the analyst's free-text final comment shown next to
   * the RCA. Pass `null` or an empty string to clear. The backend
   * derives `updated_by` from the auth token — clients cannot spoof it.
   */
  async saveAnalystComment(
    analysisId: number,
    comment: string | null,
  ): Promise<{
    analysis_id: number;
    analyst_comment: string | null;
    analyst_comment_updated_at: string | null;
    analyst_comment_updated_by: string | null;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/by-id/comment`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ analysis_id: analysisId, comment }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to save comment (${response.status})`);
    }
    return response.json();
  },

  /**
   * Save (or clear) the user's pre-analysis comment as a note-only edit.
   * Does NOT re-run analysis — to influence a new analysis, call
   * `analyzeStream` with `forceRefresh=true` and the new `userComment`.
   */
  async saveUserInputComment(
    analysisId: number,
    comment: string | null,
  ): Promise<{
    analysis_id: number;
    user_input_comment: string | null;
    user_input_comment_updated_at: string | null;
    user_input_comment_updated_by: string | null;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/by-id/user-comment`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ analysis_id: analysisId, comment }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to save comment (${response.status})`);
    }
    return response.json();
  },

  /**
   * Delete a specific analysis by ID.
   */
  async deleteById(analysisId: number): Promise<{ deleted: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/by-id`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ analysis_id: analysisId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to delete analysis (${response.status})`);
    }

    return response.json();
  },

  /**
   * Delete all analyses for a specific ticket.
   */
  async deleteByTicket(ticketKey: string): Promise<{ deleted: number }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/${encodeURIComponent(ticketKey)}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to delete analyses (${response.status})`);
    }

    return response.json();
  },

  // ── Module Query Hints ─────────────────────────────────────────────────
  //
  // Analyst-curated corrections captured from the AI Summary panel. Each
  // hint maps a piece of selected text to one of three kinds of correction:
  //   * module       — "this text identifies module X"
  //   * entity_type  — "this text should be tagged as entity type Y"
  //   * entity_value — "this text binds to table T column C"
  // See rust-backend/src/db/module_hints_store.rs for the full schema.

  async createHint(
    payload: {
      hint_type: 'module' | 'entity_value' | 'entity_type';
      selected_text: string;
      module_name?: string;
      table_fqn?: string;
      field_name?: string;
      corrected_entity_type?: string;
      source_ticket?: string;
    },
  ): Promise<ModuleHint> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/hints`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to save hint (${response.status})`);
    }
    return response.json();
  },

  async listHints(opts: { module?: string; includeDisabled?: boolean } = {}): Promise<{ hints: ModuleHint[] }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (opts.module) params.set('module', opts.module);
    if (opts.includeDisabled) params.set('include_disabled', 'true');
    const qs = params.toString();
    const url = `${API_BASE}/issue-analysis/hints${qs ? `?${qs}` : ''}`;
    const response = await fetchApi(url, { headers });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to list hints (${response.status})`);
    }
    return response.json();
  },

  async deleteHint(id: number): Promise<ModuleHint> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/hints/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to disable hint (${response.status})`);
    }
    return response.json();
  },

  // ── Entity Type Definitions (analyst-managed master) ───────────────────
  //
  // Lets analysts add new entity types from the Setup → Entity Types tab
  // without an engineering change. Pairs with the existing hint
  // endpoints: a new EntityType registered here is immediately usable in
  // the "Tag as Entity Type" dropdown inside the AI Summary panel.

  async createEntityType(payload: {
    name: string;
    aliases?: string;
    tier?: number;
    match_op?: 'eq' | 'like';
    queryable?: boolean;
    column_candidates?: string;
    description?: string;
  }): Promise<EntityTypeDefinition> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/entity-types`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to create entity type (${response.status})`);
    }
    return response.json();
  },

  async listEntityTypes(opts: { includeDisabled?: boolean } = {}): Promise<{ entity_types: EntityTypeDefinition[] }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (opts.includeDisabled) params.set('include_disabled', 'true');
    const qs = params.toString();
    const url = `${API_BASE}/issue-analysis/entity-types${qs ? `?${qs}` : ''}`;
    const response = await fetchApi(url, { headers });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to list entity types (${response.status})`);
    }
    return response.json();
  },

  async deleteEntityType(id: number): Promise<EntityTypeDefinition> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/entity-types/${id}`, {
      method: 'DELETE',
      headers,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to disable entity type (${response.status})`);
    }
    return response.json();
  },

  // ── Per-table smoke-test query ──────────────────────────────────────────
  //
  // Runs `SELECT TOP 50 * FROM <table> WHERE <col>=<value> AND ...` using
  // the bindings the analyst has just set, against the LRP service for
  // the ticket's client. Powers the "Run Query" button next to each
  // table in the table-selection UI so the analyst can verify their
  // bindings before clicking the big Analyze button.

  async runTableQuery(payload: {
    ticket_key: string;
    table_fqn: string;
    bindings: { value: string; column: string }[];
  }): Promise<{
    sql: string;
    row_count: number;
    rows: Record<string, unknown>[];
    error: string | null;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/run-table-query`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Run-table-query failed (${response.status})`);
    }
    return response.json();
  },

  // ── Free-form SQL editor (SQL Investigation section) ─────────────────────
  //
  // Runs an analyst-authored SELECT for the ticket's LRP client. The backend
  // validates the SQL (single SELECT/WITH, no DDL/DML), wraps it in a row
  // cap, and returns the rows plus the SQL it actually executed.

  async runSql(payload: {
    ticket_key: string;
    sql: string;
    row_limit?: number;
  }, signal?: AbortSignal): Promise<{
    sql: string;
    row_count: number;
    rows: Record<string, unknown>[];
    error: string | null;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/run-sql`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Run-sql failed (${response.status})`);
    }
    return response.json();
  },

  // Regenerate the Final Analysis using analyst-curated SQL datasets in
  // place of Phase 2's auto-generated `sql_results`. The cached summary is
  // overwritten so the Final Analysis tab picks up the new RCA on next load.
  async analyzeWithData(payload: {
    analysis_id: number;
    analyst_notes?: string;
    datasets: { label: string; sql: string; rows: Record<string, unknown>[]; kind?: 'data' | 'timeline' }[];
  }): Promise<{ analysis_id: number; ticket_key: string; summary: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/analyze-with-data`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Analyze-with-data failed (${response.status})`);
    }
    return response.json();
  },

  // Auto-seed the pre-analysis SQL Investigation pane. Asks the backend
  // to feed the ticket summary + entities + selected tables (with column
  // descriptions) to the LLM, which produces up to 10 compound SELECTs.
  // The backend validates, caps, and executes each query against LRP and
  // returns the cards directly.
  async seedPreCards(payload: {
    ticket_key: string;
    ticket: StructuredTicket;
    modules: string[];
    entities: ExtractedEntity[];
    tables: string[];
    row_limit?: number;
  }, signal?: AbortSignal): Promise<{
    cards: {
      label: string;
      sql: string;
      rows: Record<string, unknown>[];
      row_count: number;
      kind: string;
      rationale: string;
      error: string | null;
    }[];
    /** Selected tables that got no deterministic probe, with the reason. */
    skipped_tables?: { fqn: string; reason: string }[];
    /** Stage 0.5 semantic table suggestions (empty unless the backend
     *  enable_semantic_stage_0_5 toggle is on). Suggestions only — the
     *  backend never probes a table the analyst did not select. */
    semantic_suggestions?: {
      table_fqn: string;
      matched_field: string;
      score: number;
      already_selected: boolean;
      /** Score cleared the auto-include bar: the seeder added this table
       *  to the probing set itself (badged in the UI). */
      auto_included: boolean;
    }[];
    /** Stage 0.6 structural table suggestions (empty unless the backend
     *  enable_structural_stage_0_6 toggle is on). A ticket word resolved
     *  directly against Compass's real schema catalog, corroborated by a
     *  real source-procedure sweep — not an embedding match. */
    structural_suggestions?: {
      table_fqn: string;
      matched_term: string;
      score: number;
      source_hits: number;
      already_selected: boolean;
      auto_included: boolean;
    }[];
    debug_prompt_preview?: string;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/seed-pre-cards`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Seed-pre-cards failed (${response.status})`);
    }
    return response.json();
  },

  // Fetch the persisted auto-seeded cards for a ticket (stored by the
  // backend when seed-pre-cards completes). Used to restore the Saved
  // Results pane when a saved analysis is reopened.
  async getPreCards(ticketKey: string, signal?: AbortSignal): Promise<{
    ticket_key: string;
    cards: {
      label: string;
      sql: string;
      rows: Record<string, unknown>[];
      row_count: number;
      kind: string;
      rationale: string;
      error: string | null;
    }[];
    skipped_tables: { fqn: string; reason: string }[];
    seeded_at: string | null;
  }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(
      `${API_BASE}/issue-analysis/${encodeURIComponent(ticketKey)}/pre-cards`,
      { method: 'GET', headers, signal },
    );
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Fetch pre-cards failed (${response.status})`);
    }
    return response.json();
  },

  // Persist analyst entity corrections (edit/remove/add) on a stored
  // analysis — they survive reloads and feed later re-seeds.
  /** Vouch for an entity value permanently.
   *
   *  Editing an entity fixes one ticket; confirming it is remembered, so the
   *  same value on a later ticket is no longer doubted. Recorded per carrier. */
  async confirmEntityValue(
    ticketKey: string,
    entityType: string,
    value: string,
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/entities/confirm`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ticket_key: ticketKey, entity_type: entityType, value }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Confirm failed (${response.status})`);
    }
  },

  async updateEntities(analysisId: number, entities: ExtractedEntity[]): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/${analysisId}/entities`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(entities),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Update entities failed (${response.status})`);
    }
  },

  async generateFinalSummary(analysisResult: IssueAnalysisResponse, signal?: AbortSignal, forceRefresh = false): Promise<{ ticket_key: string; summary: string; classification?: string | null; cached?: boolean }> {
    if (!analysisResult.analysis_id) {
      throw new Error('Cannot generate final summary: analysis has no saved ID. Try re-running the analysis.');
    }
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/final-summary`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        analysis_id: analysisResult.analysis_id,
        force_refresh: forceRefresh,
      }),
      signal,
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to generate final summary (${response.status})`);
    }

    return response.json();
  },

  async generateRcaVisualizations(analysisId: number): Promise<{ ticket_key: string; analysis_id: number; gantt: string; causal_chain: string; action_flow: string }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${API_BASE}/issue-analysis/rca-visualizations`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ analysis_id: analysisId }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => null);
      throw new Error(data?.detail || `Failed to generate RCA visualizations (${response.status})`);
    }

    return response.json();
  },
};
